'use client'

import type { ReactNode } from 'react'

import { Button } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import { ContactAdminButton } from './contact-admin-button'

interface NotAllowedOverlayProps {
  children: ReactNode
  role?: string
  allowedRoles?: string[]
  /**
   * Verdict for gates a role alone cannot express — "does this guru teach this class?", for one.
   * Wins over `role` / `allowedRoles` when passed.
   */
  allowed?: boolean
  className?: string
  /** Card heading. Defaults to the school-admin wording. */
  title?: string
  /** Card body. Takes a node, not a string, so callers can emphasize part of the sentence. */
  description?: ReactNode
  /** Back button label. */
  backLabel?: string
  /**
   * Offers "Hubungi admin" beside the back button, for gates whose way out is somebody else
   * assigning you a class.
   *
   * Opt-in rather than default: this overlay also covers "Kamu tidak mengampu kelas ini", where the
   * guru already teaches elsewhere and has nothing to ask for. The button gates itself on role and
   * class count too, so a passing page shows it only to a member with no rombel at all.
   */
  withContactAdmin?: boolean
  /**
   * Runs when the back button is pressed. Defaults to `window.history.back()`.
   * Pass a router-aware handler on pages that need a guaranteed destination — someone who opened
   * the URL directly has no in-app entry to go back to.
   */
  onBack?: () => void
}

const DEFAULT_TITLE = 'Ups! Kamu bukan admin sekolah ini'

const DEFAULT_DESCRIPTION = (
  <>
    Hanya admin sekolah yang dapat mengatur dan mengelola anggotanya. Jika kamu belum ditempatkan mengajar kepada murid
    mana pun, <strong className={typographyVariants({ variant: 'title-md' })}>coba hubungi admin sekolah ini.</strong>
  </>
)

export function NotAllowedOverlay({
  children,
  role,
  allowedRoles,
  allowed,
  className,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  backLabel = 'Kembali',
  withContactAdmin = false,
  onBack,
}: NotAllowedOverlayProps) {
  const isAllowed = allowed ?? (role !== undefined && (allowedRoles?.includes(role) ?? false))

  if (isAllowed) {
    return <>{children}</>
  }

  const handleBack = () => {
    if (onBack) {
      onBack()
      return
    }
    window.history.back()
  }

  return (
    <div className={cn('srd:relative srd:h-full srd:min-h-screen srd:w-full', className)}>
      <div className='srd:bg-neutrals-white/20 srd:absolute srd:inset-0 srd:z-50 srd:flex srd:h-full srd:min-h-screen srd:w-full srd:cursor-not-allowed srd:items-center srd:justify-center srd:p-4 srd:backdrop-blur-sm'>
        <div className='srd:bg-neutrals-white srd:flex srd:w-full srd:max-w-[560px] srd:cursor-default srd:flex-col srd:items-center srd:justify-center srd:gap-y-2 srd:rounded-3xl srd:px-6 srd:py-10 srd:text-center srd:shadow-lg'>
          <h2 className={cn(typographyVariants({ variant: 'headline-sm' }), 'srd:text-neutral-black')}>{title}</h2>

          <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-grey-100')}>{description}</p>

          <div className='srd:mt-4 srd:flex srd:flex-wrap srd:items-center srd:justify-center srd:gap-2'>
            <Button className='srd:px-8' onClick={handleBack} type='button'>
              {backLabel}
            </Button>

            {/* Secondary to Kembali, which stays this card's primary action. */}
            {withContactAdmin && <ContactAdminButton variant='outline' className='srd:px-8' />}
          </div>
        </div>
      </div>

      {children}
    </div>
  )
}
